export enum AlertType {
    Info = 'info',
    Success = 'success',
    Error = 'error',
    Warning = 'warning',
}

export type Alert = {
    action?: 'cart' | 'wishlist';
    type: AlertType;
    message: string;
};

export type Category = {
    id: string;
    name: string;
    description?: string;
    url: string;
};

export type FillerType = {
    id: string;
    name: string;
    url: string;
    description?: string;
};

export type FabricType = {
    id: string;
    name: string;
    url: string;
    description?: string;
};

export interface PromoCode {
    id: string;
    code: string;
    discountType: 'percentage' | 'points';
    discountValue: number;
    type: 'global' | 'personal';
    phone?: string;
    usageLimit?: number;
    usageCount: number;
    status: 'active' | 'inactive';
    createdAt: string;
}

export type Option = {
    [key: string]: string;
};

export type PromoKey = 'promo' | 'season' | 'sale' | 'blank';

export type Product = {
    id: string;
    slug: string;
    category: {
        name: string;
        id: string;
        url: string;
    };
    description: string;
    shortDescription?: string;
    discount?: {
        percent: number;
        discountedPrice: number;
    } | null;
    specialDiscount?: {
        percent: number;
        discountedPrice: number;
    } | null;
    image: any;
    secondImage: any;
    thirdImage: any;
    fourthImage: any;
    fifthImage: any;
    sixthImage: any;
    name: string;
    fillerType: {
        name: string;
        id: string;
        url?: string;
    } | null;
    fabricType: {
        name: string;
        id: string;
        url?: string;
    } | null;
    price: number;
    weight: number;
    grossWeight: number;
    isLocked?: boolean;
    quantity: number;
    rawMaterial?: string;
    careInstructions?: string;
    brandName?: string;
    countryOrigin?: string;
    sellerName?: string;
    articleNumber?: string;
    productСolor?: string;
    nameDescription?: string;
    priority?: number;
    reviewsEnabled?: boolean;
    dimensions?: {
        length: number;
        width: number;
        height: number;
    };
    complectation?: string;
    group?: {
        id: string;
        name: string;
        url: string;
        optionValue: string;
    } | null;
    season?: string | null;
    promoType?: PromoKey | null;
    crptAvailable?: number;
};

export type Error = {
    isError: boolean;
    message: string;
};

export type CartItem = {
    productId: Product['id'];
    name: Product['name'];
    nameDescription?: Product['nameDescription'];
    price: Product['price'];
    quantity: number;
    stockQuantity?: number;
    totalPrice: number;
    weight: Product['weight'];
    totalWeight: number;
    profit?: number;
    discount?: number;
    discountedPrice?: number;
    specialDiscountedPrice?: number;
    specialDiscountPercent?: number;
    shippingMethod?: 'pickup' | 'dpd' | 'dtl';
    shippingCost?: number | null;
    articleNumber?: Product['articleNumber'];
    group?: Product['group'];
    markingCodes?: string[] | null;
    lineId?: string;

    threePlusApplied?: boolean;
    secondPillowAppliedQty?: number;
    secondPillowDiscountPercent?: number;
    secondPillowDiscountAmount?: number;
};

export type ProductCartItem = CartItem & {
    name: Product['name'];
    categoryUrl: Product['category']['url'];
    image: Product['image'];
    isWished: boolean;
    availableQuantity: number;
    dimensions?: {
        length: number;
        width: number;
        height: number;
    };
};

export type AppliedPromo = {
    code: string;
    discountType: 'percentage' | 'points';
    discountValue: number;
};

export type PaymentMethod = 'bank_card' | 'sbp' | 'sber_bnpl';

export type AtolDeliveryMode = 'ofd_email' | 'ofd_phone' | 'self';

export type AtolReceiptPayload = {
    total?: number;
    fnsSite?: string;
    fnNumber?: string;
    shiftNumber?: number | string;
    receiptDatetime?: string;
    fiscalReceiptNumber?: number | string;
    fiscalDocumentNumber?: number | string;
    ecrRegistrationNumber?: string;
    fiscalDocumentAttribute?: number | string;
    ofdReceiptUrl?: string;
} | null;

export type AtolReceiptInfo = {
    provider: 'atol';
    status: string;
    externalId?: string | null;
    uuid?: string | null;
    deliveryMode?: AtolDeliveryMode;
    completedAt?: string | null;
    selfDeliveredAt?: string | null;
    selfDeliveredVia?: 'email' | 'sms' | null;
    selfDeliveredTo?: string | null;
    payload?: AtolReceiptPayload;
} | null;

export type YooKassaMoney = {
    value: string;
    currency: string;
};

export type YooKassaReceiptRecord = {
    id?: string;
    type?: string;
    status?: string;
    payment_id?: string | null;
    refund_id?: string | null;
    created_at?: string | null;
    registered_at?: string | null;
    fiscal_document_number?: number | string | null;
    fiscal_document_attribute?: number | string | null;
    [key: string]: unknown;
};

export type OrderRefundReceiptsInfo = {
    id: string;
    status: string | null;
    amount: YooKassaMoney | null;
    createdAt: string | null;
    receiptRegistration: unknown;
    receipts: YooKassaReceiptRecord[];
    warning?: string;
};

export type OrderReceiptsResponse = {
    orderNumber: string;
    paymentId: string | null;
    paymentMethod: string | null;
    paymentStatus: string | null;
    orderStatus: string | null;
    paymentReceiptRegistration: unknown;
    paymentReceipts: YooKassaReceiptRecord[];
    refunds: OrderRefundReceiptsInfo[];
    legacyAtol: AtolReceiptInfo;
    warnings: string[];
};

export type ThreePlusPromoMeta = {
    key: string;
    discountAmount: number;
    validUntil: string | null;
};

export type SecondPillowPromoMeta = {
    key: string;
    discountAmount: number;
    validUntil?: string | null;
    categoryUrl?: string | null;
};

export type Order = {
    id: string;
    timestamp: number;
    user: {
        name: string;
        phone: string;
        email?: string;
        address: AddressContainer | null;
    };
    cart: Array<CartItem & { threePlusApplied?: boolean }>;
    totalPrice: number;
    totalWeight: number;
    birthdayDiscount: boolean;
    paidWithPoints: number | null;
    totalDiscount: number;
    totalQuantity: number;
    orderNumber: string;
    userID: string;
    orderStatus: string;
    orderComment: string;
    promoCode: AppliedPromo | null;
    paymentMethod?: PaymentMethod;
    atol?: AtolReceiptInfo;
    delivery?: Delivery | null;
    threePlusPromo?: ThreePlusPromoMeta | null;
    markingCode?: string | null;
    secondPillowPromo?: SecondPillowPromoMeta | null;
};

export type Delivery = {
    method: 'dpd' | 'dtl';
    cost: number;
    orderNum: string | null;
};

export interface AddressStruct {
    postalCode: string;
    region: string;
    city: string;
    cityType: string;
    dpdCityId: number | null;
    street: string;
    streetType: string;
    streetId: string | null;
    house: string;
    block: string;
    flat: string;
    geoLat?: number;
    geoLon?: number;
}

export interface AddressContainer {
    raw: string;
    struct: AddressStruct | null;
    comment?: string;
}

export interface AddressValidationResponse {
    deliverable: boolean;
    reason?: string;
}

export interface UserCardInfo {
    cardNumber: string;
    level: 'base' | 'silver' | 'gold' | 'platinum' | string;
    discount: number;
    balance: number;
    status: string;
    lastAuthDate?: string;
}

export interface UserDateOfBirth {
    date: string;
    updatedAt?: string;
}

export interface User {
    _id: string;
    lastName: string;
    firstName: string;
    middleName?: string;
    dateOfBirth?: UserDateOfBirth;
    phoneNumber: string;
    phoneNumberStatus: boolean;
    email: string;
    emailStatus: boolean;
    address: AddressContainer | null;
    addresses?: Array<AddressContainer & { _id: string; isDisabled?: boolean }>;
    addressRaw?: string;
    addressStruct?: AddressStruct;
    card: UserCardInfo;
    marketing?: {
        discounts_and_promotions: boolean;
        createdAt?: string;
        updatedAt?: string;
    } | null;
}

export interface CitySuggestionApi {
    dpdCityId?: number;
    cityId?: number;
    id?: number | string;
    city?: string;
    cityName?: string;
    name?: string;
    region?: string;
    regionName?: string;
    postalCode?: string;
    index?: string;
    abbreviation?: string;
    cityType?: string;
    geoLat?: number;
    lat?: number;
    geoLon?: number;
    lon?: number;
}

export interface StreetSuggestionApi {
    fiasId?: string;
    streetId?: string;
    id?: string;
    street?: string;
    name?: string;
    streetType?: string;
    type?: string;
    value?: string;
}

export interface HouseSuggestionApi {
    house?: string;
    value?: string;
}

export type ProdGroup = {
    id: string;
    name: string;
    description: string;
    url: string;
};
